import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_ICON_WIDTH } from './constants';

export type SidebarMode = 'full' | 'icon-only';

interface SidebarV2ContextType {
    mode: SidebarMode;
    setMode: (mode: SidebarMode) => void;
    toggleMode: () => void;
    sidebarWidth: number;
    /** Width of the currently mounted nested sidebar (0 if none). */
    nestedSidebarWidth: number;
    setNestedSidebarWidth: (width: number) => void;
    /** Total offset for content: primary sidebar + nested sidebar. */
    totalSidebarWidth: number;
}

const SidebarV2Context = createContext<SidebarV2ContextType | undefined>(undefined);

export const SidebarV2Provider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [mode, setMode] = useState<SidebarMode>('icon-only');
    const [nestedSidebarWidth, setNestedSidebarWidth] = useState(0);

    const toggleMode = useCallback(() => {
        setMode(prev => (prev === 'full' ? 'icon-only' : 'full'));
    }, []);

    const sidebarWidth = mode === 'full' ? SIDEBAR_FULL_WIDTH : SIDEBAR_ICON_WIDTH;
    const totalSidebarWidth = sidebarWidth + nestedSidebarWidth;

    return (
        <SidebarV2Context.Provider value={{ mode, setMode, toggleMode, sidebarWidth, nestedSidebarWidth, setNestedSidebarWidth, totalSidebarWidth }}>
            {children}
        </SidebarV2Context.Provider>
    );
};

export const useSidebarV2 = () => {
    const context = useContext(SidebarV2Context);
    if (!context) {
        throw new Error('useSidebarV2 must be used within a SidebarV2Provider');
    }
    return context;
};
